import moment from 'moment'
import { z } from 'zod'

import { stringToDate } from 'modules/core/lib/dates'
import { toCents } from 'modules/core/lib/formatCurrencyUtils'
import i18n from 'modules/core/lib/i18n'
import MoneyWithCurrency from 'modules/core/lib/money'

const MAXIMUM_COST = 10000
const MAXIMUM_LENGTH = 100
const MAXIMUM_FILE_SIZE = 4 * 1024 * 1024

// Custom validation helpers
const isValidFile = (file: File | undefined): file is File => {
  return file instanceof File && file.size > 0
}

// Step 1: Plan Details Schema
export const planDetailsStepSchema = () =>
  z.object({
    providerName: z
      .string()
      .trim()
      .min(1, i18n.t('benefits.healthcare.plan_wizard.validation.common.required'))
      .max(
        MAXIMUM_LENGTH,
        i18n.t('benefits.healthcare.plan_wizard.validation.common.max_length', {
          max: MAXIMUM_LENGTH,
        })
      ),

    planName: z
      .string()
      .trim()
      .min(1, i18n.t('benefits.healthcare.plan_wizard.validation.common.required'))
      .max(
        MAXIMUM_LENGTH,
        i18n.t('benefits.healthcare.plan_wizard.validation.common.max_length', {
          max: MAXIMUM_LENGTH,
        })
      ),
    renewalDate: z.string().refine((date) => {
      const renewalDate = stringToDate(date, 'ui')
      const today = moment()
      return renewalDate >= today
    }, i18n.t('benefits.healthcare.plan_wizard.validation.renewal_date.future_date')),

    waitingPeriodMonths: z
      .number()
      .int(i18n.t('benefits.healthcare.plan_wizard.validation.common.invalid_amount'))
      .min(0, i18n.t('benefits.healthcare.plan_wizard.validation.common.negative'))
      .max(12, i18n.t('benefits.healthcare.plan_wizard.validation.waiting_period.max_months')),
    allowPreExistingConditions: z.boolean(),

    planDocument: z
      .custom<File>()
      .superRefine((file, ctx) => {
        if (!isValidFile(file)) {
          ctx.addIssue({
            code: z.ZodIssueCode.custom,
            message: i18n.t('benefits.healthcare.plan_wizard.validation.common.required'),
            fatal: true,
          })
        }
      })
      .refine(
        (file) => file.size <= MAXIMUM_FILE_SIZE,
        i18n.t('benefits.healthcare.plan_wizard.validation.plan_document.file_size', {
          size: MAXIMUM_FILE_SIZE,
        })
      )
      .refine(
        (file) =>
          [
            'application/pdf',
            'application/msword',
            'application/vnd.openxmlformats-officedocument.wordprocessingml.document',
          ].includes(file.type),
        i18n.t('benefits.healthcare.plan_wizard.validation.plan_document.file_type')
      ),
  })

// Step 2: Employee Cost Schema (base schema without refinements)
const employeeCostStepBaseSchema = (locale: string, currency: string) =>
  z.object({
    employeeCostCents: z
      .number()
      .int(i18n.t('benefits.healthcare.plan_wizard.validation.common.invalid_amount'))
      .min(0, i18n.t('benefits.healthcare.plan_wizard.validation.common.negative'))
      .max(
        toCents(MAXIMUM_COST),
        i18n.t('benefits.healthcare.plan_wizard.validation.common.maximum_exceeded', {
          max: MoneyWithCurrency({
            amount: toCents(MAXIMUM_COST),
            currency,
            locale,
          }),
        })
      ),

    coveredEmployeeCostCents: z
      .number()
      .int(i18n.t('benefits.healthcare.plan_wizard.validation.common.invalid_amount'))
      .min(0, i18n.t('benefits.healthcare.plan_wizard.validation.common.negative')),
  })

// Step 2: Employee Cost Schema with refinements
export const employeeCostStepSchema = (locale: string, currency: string) =>
  employeeCostStepBaseSchema(locale, currency).refine(
    (data) => data.coveredEmployeeCostCents <= data.employeeCostCents,
    {
      message: i18n.t(
        'benefits.healthcare.plan_wizard.validation.covered_employee_cost.exceeds_total'
      ),
      path: ['coveredEmployeeCostCents'],
    }
  )
// Step 3: Dependents Schema (base schema without refinements)
const dependentsStepBaseSchema = (locale: string, currency: string) =>
  z.object({
    allowDependents: z.boolean(),
    dependentCostCents: z
      .number()
      .int(i18n.t('benefits.healthcare.plan_wizard.validation.common.invalid_amount'))
      .min(0, i18n.t('benefits.healthcare.plan_wizard.validation.common.negative')) // Must be >= 0 if provided
      .max(
        toCents(MAXIMUM_COST),
        i18n.t('benefits.healthcare.plan_wizard.validation.common.maximum_exceeded', {
          max: MoneyWithCurrency({
            amount: toCents(MAXIMUM_COST),
            currency,
            locale,
          }),
        })
      )
      .optional(),

    coveredDependentCostCents: z
      .number()
      .int(i18n.t('benefits.healthcare.plan_wizard.validation.common.invalid_amount'))
      .min(0, i18n.t('benefits.healthcare.plan_wizard.validation.common.negative')) // Must be >= 0 if provided
      .optional(),
  })

// Step 3: Dependents Schema with refinements
export const dependentsStepSchema = (locale: string, currency: string) =>
  dependentsStepBaseSchema(locale, currency)
    .refine(
      (data) => {
        // If "No" is selected, skip this validation
        if (data.allowDependents === false) {
          return true
        }

        return data.dependentCostCents !== undefined
      },
      {
        message: i18n.t('benefits.healthcare.plan_wizard.validation.common.invalid_amount'),
        path: ['dependentCostCents'],
      }
    )
    .refine(
      (data) => {
        // If dependents are enabled, covered cost cannot exceed total cost
        if (data.allowDependents === false) {
          return true
        }
        if (data.coveredDependentCostCents !== undefined && data.dependentCostCents !== undefined) {
          return data.coveredDependentCostCents <= data.dependentCostCents
        }

        return false
      },
      {
        message: i18n.t(
          'benefits.healthcare.plan_wizard.validation.covered_dependent_cost.exceeds_total'
        ),
        path: ['coveredDependentCostCents'],
      }
    )

export const healthcarePlanWizardSchema = (locale: string, currency: string) =>
  planDetailsStepSchema()
    .and(employeeCostStepSchema(locale, currency))
    .and(dependentsStepSchema(locale, currency))
